import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from "@mui/material";
import { type GridRowSelectionModel } from "@mui/x-data-grid";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useListMailCategoriesQuery } from "api/memberPortal/mailList/mailList.api";
import { MailCategory } from "api/memberPortal/mailList/mailList.types";
import SearchBar from "components/MUI/DataGrid/Detached/SearchBar";

import CreateCategoryForm from "./components/CreateCategory/CreateCategory.form";
import MailListCategoriesTable from "./components/MailListCategories.table";

export default function MailListCategories() {
  const navigate = useNavigate();

  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<
    MailCategory | undefined
  >();
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [searchText, setSearchText] = useState("");

  const { data: categories, isLoading } = useListMailCategoriesQuery({
    limit: 100,
    search: searchText,
  });
  if (!categories) return <></>;
  if (isLoading) return <></>;

  const handleResetTable = () => {
    setSelectedRows([]);
  };

  const handleSelectingCategory = (category: GridRowSelectionModel) => {
    setSelectedRows(category);
  };

  const handleClose = () => {
    handleResetTable();
    navigate("..");
  };

  const handleFormOpen = () => {
    handleResetTable();
    setFormOpen(true);
  };

  const handleFormClose = () => {
    handleResetTable();
    setFormOpen(false);
  };

  const handleEdit = (category: MailCategory) => {
    handleResetTable();
    handleFormOpen();
    setSelectedCategory(category);
  };

  const onClose = () => {
    handleResetTable();
    setSelectedCategory(undefined);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
  };

  return (
    <Dialog open fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Category Manager
        <IconButton onClick={handleClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pb: 10 }}>
        <Stack spacing={2}>
          <Collapse in={formOpen} onExited={onClose}>
            <Card>
              <CardHeader
                subheader={`${selectedCategory ? "Editing" : "New"} Category`}
              />
              <CardContent>
                <CreateCategoryForm
                  key={selectedCategory?.id ?? "create"}
                  onCreate={handleFormClose}
                  onCancel={handleFormClose}
                  selectedCategory={selectedCategory}
                  onUpdate={handleFormClose}
                />
              </CardContent>
            </Card>
          </Collapse>
          <Grid2 container gap={2}>
            <Grid2 size={8}>
              <SearchBar
                onSearchChange={handleSearchChange}
                sx={{ width: "100%" }}
              />
            </Grid2>
            <Grid2 size={3}>
              <Button
                size="medium"
                startIcon={<AddCircleOutlineIcon />}
                variant="outlined"
                onClick={handleFormOpen}
                sx={{
                  opacity: formOpen ? 0 : 1,
                  pointerEvents: formOpen ? "none" : "auto",
                  transition: "opacity 200ms",
                }}
              >
                Add Category
              </Button>
            </Grid2>
          </Grid2>
          <Box sx={{ mt: 5 }}>
            <MailListCategoriesTable
              categoryData={categories.results}
              isLoading={isLoading}
              onEdit={handleEdit}
              selectedRows={selectedRows}
              onSelection={handleSelectingCategory}
              handleClearSelection={handleResetTable}
            />
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
